import { useEffect, useRef, useState } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

interface UseUnifiedMapOptions {
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Free, no-key vector basemap (attribution baked into the style). Promoted from
 * fallback to primary: CARTO light_all, which used to be the primary raster
 * source, now stamps every unauthenticated tile with an "API KEY REQUIRED"
 * watermark while still returning HTTP 200 with a valid PNG, and their keyed free
 * tier is non-commercial only.
 *
 * That HTTP 200 is also why the old availability probe was dropped along with it —
 * the probe only checked that an image loaded, which a watermarked tile does, so
 * it could never have triggered this swap on its own.
 */
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

/**
 * Single MapLibre instance shared between the world overview and country
 * drill-downs (REFACTOR_PLAN.md). Created ONCE with world defaults — never
 * re-created when the active country changes. Drill-in adjusts viewport /
 * minZoom via mapRef directly (added in steps 3+), so layer crossfades can
 * stay on the GPU instead of being broken up by unmount/remount.
 *
 * Style + controls match the legacy useMapLibre / WorldView setup so the
 * visual baseline is identical when USE_UNIFIED is toggled.
 */
export function useUnifiedMap({ containerRef }: UseUnifiedMapOptions) {
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new MapLibreMap({
      container: containerRef.current,
      style: BASEMAP_STYLE,
      center: [10, 25],
      zoom: 1.5,
      attributionControl: { compact: true },
      minZoom: 1,
      maxZoom: 14,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.on('load', () => setReady(true));
    mapRef.current = map;

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { mapRef, ready };
}
